/**
 * Строка решения «Авто» над ответом на языке интерфейса (agroai-wo4).
 *
 * Сервер (`ai_agent/auto.py`, `Decision.label`) пишет строку по-русски
 * («Авто → Ответ · кто отстаёт») и кладёт её в метаданные ответа вместе с
 * частями решения: намерением, плейбуком, экраном. Для русского интерфейса
 * показывается строка сервера как есть; для остальных она собирается здесь из
 * тех же частей по тем же правилам. Неизвестная часть (новое намерение или
 * плейбук, которых эта версия интерфейса не знает) — строка сервера.
 * Русские тексты сообщений равны серверным — их сверяет
 * `backend/tests/test_ai_agent_panel_captions.py`.
 */
import { defineMessages, type PlainMessage } from '../i18n/define';
import { tr } from '../i18n/runtime';
import { screenTitle } from './screenTitles';

const m = defineMessages({
  only: { id: 'ai.route.auto.only', defaultMessage: 'Авто → {intent}', description: 'Строка над ответом ИИ в режиме «Авто»: что выбрал ассистент; {intent} — вид ответа («Ответ», «Гид», «Отчёт»)' },
  with: { id: 'ai.route.auto.with', defaultMessage: 'Авто → {intent} · {detail}', description: 'Строка над ответом ИИ в режиме «Авто» с уточнением; {intent} — вид ответа, {detail} — что именно (плейбук, экран)' },
  answer: { id: 'ai.route.intent.answer', defaultMessage: 'Ответ', description: 'Вид ответа в строке «Авто → …»: ответ числом и словами; как режим «Ответ»' },
  onScreen: { id: 'ai.route.intent.alreadyOnScreen', defaultMessage: 'Подсказка', description: 'Вид ответа в строке «Авто → …»: ответ уже виден на экране — показать где; как режим «Подсказка»' },
  guide: { id: 'ai.route.intent.guideTour', defaultMessage: 'Гид', description: 'Вид ответа в строке «Авто → …»: провести по экранам; как режим «Гид»' },
  report: { id: 'ai.route.intent.report', defaultMessage: 'Отчёт', description: 'Вид ответа в строке «Авто → …»: собрать отчёт; как режим «Отчёт»' },
  reportEdit: { id: 'ai.route.intent.reportEdit', defaultMessage: 'Правка отчёта', description: 'Вид ответа в строке «Авто → …»: изменить уже собранный отчёт беседы' },
  detailReportEdit: { id: 'ai.route.detail.reportEdit', defaultMessage: 'отчёт беседы', description: 'Уточнение в строке «Авто → Правка отчёта · …»: правится отчёт этой беседы; строчными' },
  detailCustom: { id: 'ai.route.detail.custom', defaultMessage: 'свой план', description: 'Уточнение в строке «Авто → Отчёт · …»: отчёт по своему плану блоков, не по готовому образцу; строчными' },
  detailScreens: { id: 'ai.route.detail.screens', defaultMessage: 'по экранам', description: 'Уточнение в строке «Авто → Гид · …»: тур по экранам; строчными' },
  detailScreen: { id: 'ai.route.detail.screen', defaultMessage: 'экран «{title}»', description: 'Уточнение в строке «Авто → Подсказка · …»: на каком экране ответ; {title} — название экрана; строчными' },
  detailScreenPlus: { id: 'ai.route.detail.screenPlus', defaultMessage: '+ экран «{title}»', description: 'Уточнение в строке «Авто → Ответ · …»: к ответу добавлена карточка экрана; {title} — название экрана; строчными' },
  seasonReport: { id: 'ai.route.playbook.season_report', defaultMessage: 'доклад по сезону', description: 'Готовый образец ответа в строке «Авто → Отчёт · …»: доклад по сезону для хокима; строчными' },
  campaignReport: { id: 'ai.route.playbook.campaign_report', defaultMessage: 'отчёт по кампании', description: 'Готовый образец ответа в строке «Авто → Отчёт · …»: отчёт по одной кампании; строчными' },
  platformTour: { id: 'ai.route.playbook.platform_tour', defaultMessage: 'тур по платформе', description: 'Готовый образец ответа в строке «Авто → Гид · …»: обзорный тур по пунктам меню; строчными' },
  laggards: { id: 'ai.route.playbook.laggards', defaultMessage: 'кто отстаёт', description: 'Готовый образец ответа в строке «Авто → Ответ · …»: какие хозяйства отстают с работой; строчными' },
  farmWater: { id: 'ai.route.playbook.farm_water', defaultMessage: 'вода по хозяйствам', description: 'Готовый образец ответа в строке «Авто → Ответ · …»: вода по хозяйствам; строчными' },
});

/** Намерение → вид ответа (`auto.INTENT_LABEL`). */
const INTENTS: Record<string, PlainMessage> = {
  answer: m.answer,
  already_on_screen: m.onScreen,
  guide_tour: m.guide,
  report_playbook: m.report,
  report_custom: m.report,
  report_edit: m.reportEdit,
};

/** Плейбук → его название (`auto.PLAYBOOKS[…].title`). */
const PLAYBOOKS: Record<string, PlainMessage> = {
  season_report: m.seasonReport,
  campaign_report: m.campaignReport,
  platform_tour: m.platformTour,
  laggards: m.laggards,
  farm_water: m.farmWater,
};

/** Части решения «Авто» из метаданных ответа (`Decision.public()`). */
export interface AutoParts {
  intent: string;
  playbook?: string | null;
  page?: string | null;
  page_title?: string | null;
}

const own = <T,>(table: Record<string, T>, key: string | null | undefined): T | undefined =>
  key && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;

/**
 * Строка решения на языке интерфейса — те же правила, что `Decision.label` сервера;
 * `null` — часть решения этой версии интерфейса незнакома (показать строку сервера).
 */
export function autoLabel(decision: AutoParts): string | null {
  const intent = own(INTENTS, decision.intent);
  if (!intent) return null;
  const page = decision.page_title ? (decision.page ? screenTitle(decision.page, decision.page_title) : decision.page_title) : null;
  let detail: string | null = null;
  if (decision.intent === 'report_edit') detail = tr(m.detailReportEdit);
  else if (decision.intent === 'report_custom') detail = tr(m.detailCustom);
  else if (decision.intent === 'already_on_screen' && page) detail = tr(m.detailScreen, { title: page });
  else if (decision.playbook) {
    const playbook = own(PLAYBOOKS, decision.playbook);
    if (!playbook) return null;
    detail = tr(playbook);
  } else if (decision.intent === 'guide_tour') detail = tr(m.detailScreens);
  else if (page) detail = tr(m.detailScreenPlus, { title: page });
  return detail ? tr(m.with, { intent: tr(intent), detail }) : tr(m.only, { intent: tr(intent) });
}
